// src/components/budgeting/BudgetCategoryTreeRow.jsx
import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Eye, Edit2, Trash2 } from 'lucide-react';

/**
 * BudgetCategoryTreeRow Component
 * Displays a single budget category row in tree structure with expand/collapse
 * 
 * Props:
 * - category: Budget category object
 * - level: Nesting level (0, 1, 2)
 * - onView: Callback when view clicked
 * - onEdit: Callback when edit clicked
 * - onDelete: Callback when delete clicked
 */
const BudgetCategoryTreeRow = ({ 
  category, 
  level = 0, 
  onView,
  onEdit,
  onDelete 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Calculate indentation based on level
  const indentWidth = level * 32; // 32px per level
  
  // Determine row background color based on level
  const bgColorClass = level === 0 
    ? 'bg-white hover:bg-blue-50' 
    : level === 1 
      ? 'bg-gray-50 hover:bg-blue-50' 
      : 'bg-blue-50 hover:bg-blue-100';
  
  // Toggle expand/collapse
  const toggleExpand = () => {
    if (category.isGroup && category.children?.length > 0) {
      setIsExpanded(!isExpanded);
    }
  };
  
  // Has children check
  const hasChildren = category.children && category.children.length > 0;

  return (
    <>
      {/* Current Row */}
      <tr className={`border-b border-gray-200 ${bgColorClass} transition-colors`}>
        {/* NO - Will be set by parent */}
        <td className="px-4 py-3 text-sm text-gray-900 font-medium w-16 text-center">
          {category.displayOrder || '-'}
        </td>
        
        {/* CODE & CATEGORY NAME */}
        <td className="px-4 py-3">
          <div className="flex items-center" style={{ paddingLeft: `${indentWidth}px` }}>
            {/* Expand/Collapse Button */}
            {category.isGroup && hasChildren ? (
              <button
                onClick={toggleExpand}
                className="p-1 hover:bg-gray-200 rounded transition-colors mr-2"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-6 mr-2" /> // Spacer for leaf items
            )}
            
            <div className="flex items-center gap-3 flex-1">
              {/* Code Badge */}
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                {category.categoryCode}
              </span>
              
              {/* Category Name & Details */}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900">
                  {category.categoryName}
                </div>
                
                {/* Subtitle */}
                {category.subtitle && (
                  <div className="text-xs text-gray-500 mt-1">
                    {category.subtitle}
                  </div>
                )}
                
                {/* Cost Type */}
                {category.costType && (
                  <div className="text-xs text-gray-500 mt-1">
                    <span className="font-medium">Type:</span> {category.costType}
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>
        
        {/* LEVEL */}
        <td className="px-4 py-3 text-center w-20">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
            level === 0 ? 'bg-purple-100 text-purple-800' :
            level === 1 ? 'bg-blue-100 text-blue-800' :
            'bg-green-100 text-green-800'
          }`}>
            L{level}
          </span>
        </td>
        
        {/* TYPE */}
        <td className="px-4 py-3 text-center w-24">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
            category.isGroup 
              ? 'bg-amber-100 text-amber-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {category.isGroup ? 'Group' : 'Leaf'}
          </span>
        </td>
        
        {/* CAN ADD BOQ */}
        <td className="px-4 py-3 text-center w-32">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
            category.canAddDirect 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {category.canAddDirect ? 'Yes' : 'No'}
          </span>
        </td>
        
        {/* STATUS */}
        <td className="px-4 py-3 text-center w-24">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
            category.isActive 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {category.isActive ? 'Active' : 'Inactive'}
          </span>
        </td>
        
        {/* ACTIONS */}
        <td className="px-4 py-3 w-32">
          <div className="flex items-center justify-center gap-2">
            {/* View Button */}
            <button
              onClick={() => onView && onView(category)}
              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            
            {/* Edit Button */}
            <button
              onClick={() => onEdit && onEdit(category)}
              className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
              title="Edit Category"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            
            {/* Delete Button */}
            <button
              onClick={() => onDelete && onDelete(category)}
              className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
              title="Delete Category"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      
      {/* Children Rows (Recursive) */}
      {isExpanded && hasChildren && (
        <>
          {category.children.map((child, index) => (
            <BudgetCategoryTreeRow
              key={child.id}
              category={{ ...child, displayOrder: index + 1 }}
              level={level + 1}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </>
      )}
    </>
  );
};

export default BudgetCategoryTreeRow;